import colorPallete, { labelFonts, printFonts, themeFontWeights } from "@DSG/MobiusOverrides/styles/variables";
import { getSettingsCollection } from "@DSG/Overrides/Store/Context/ContextSelectors";
import CoveoApplicationState from "@DSG/Store/Data/CoveoApplicationState/CoveoApplicationState";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { SettingsCollectionModel } from "@insite/client-framework/Services/SettingsService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { BrandDto } from "@insite/client-framework/Types/ApiModels";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    brand: BrandDto;
    /**
     * If true, the small brand logo will be displayed.
     * If false, the brand name will be displayed instead.
     * Default value: false */
    showLogo?: boolean;
    extendedStyles?: ProductBrandStyles;
    subBrand?: string;
    attributeValueId?: string | null;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
        ResolveThunks<typeof mapDispatchToProps>;

export interface ProductBrandStyles {
    logoImage?: LazyImageProps;
    nameLink?: LinkPresentationProps;
    nameText?: TypographyProps;
    brandWrapper?: InjectableCss;
    wrapper?: InjectableCss;
}
const mapStateToProps = (state: CoveoApplicationState) => {
    return {
        customSettings : getSettingsCollection(state).customSettings
    }
}
const mapDispatchToProps = {
};

const nameText: TypographyProps = {
    size: labelFonts.label1SemiBold.fontSize,
    weight: 400,
    lineHeight: labelFonts.label1SemiBold.lineHeight,
    css: css`
        ${wrapInContainerStyles}
        @media print {
            &, span {
                font-size: ${printFonts.bodySemiboldExtraSm.fontSize};
                line-height: ${printFonts.bodySemiboldExtraSm.lineHeight};
            }
        }
    `,
};

export const productBrandStyles: ProductBrandStyles = {
    wrapper: {
        css: css`
            display: flex;
            min-height: 22px;
        `
    },
    brandWrapper: {
        css: css`
            line-height: ${labelFonts.label1Regular.lineHeight};
            display: inline;
            [class*="StyledClickable"] {
                display: inline;
                &,
                > span[class*="StyledTypography"] {
                    color: ${colorPallete.grey600};
                }
            }
            [data-test-selector="subBrandSeparator"] {
                color: ${colorPallete.grey700};
                display: inline;
                position: relative;
                top: 0;
                font-weight: ${themeFontWeights.regular};
            }
        `
    },
    nameText,
    nameLink: {
        typographyProps: nameText,
    },
};

const ProductBrand: FC<Props> = ({ brand, showLogo = false, subBrand, attributeValueId, extendedStyles,customSettings }) => {
    if (!brand) {
        return null;
    }

    const [styles] = React.useState(() => mergeToNew(productBrandStyles, extendedStyles));
    const settings = customSettings as SettingsCollectionModel;
    const isCoveoEnabled = settings?.enableCoveoSearch;
    let brandDisplay;
    if (showLogo && brand.logoSmallImagePath) {
        const altText = brand.logoImageAltText || brand.name;
        brandDisplay = <LazyImage {...styles.logoImage} src={brand.logoSmallImagePath} altText={altText} />;
    } else {
        brandDisplay = brand.detailPagePath ? (
            brand.name
        ) : (
            <Typography {...styles.nameText} data-test-selector="brandName">
                {brand.name}
            </Typography>
        );
    }

    // const link="http://dakotasupplygroup.local.com:3000/brands";
    let subbrandPageLink = `${brand.detailPagePath}?attributeValueIds=${attributeValueId}`;
    if(isCoveoEnabled){
            subbrandPageLink = `${brand.detailPagePath}#f-ec_brand_name_sub=${subBrand}`;
        }

    return brand.detailPagePath ? (
        <StyledWrapper {...styles.wrapper} className="brandsOuterWrapper">
            <StyledWrapper {...styles.brandWrapper} className="brandAndSubBrandWrapper">
                {subBrand && 
                <>
                    <Link {...styles.nameLink} href={subbrandPageLink} data-test-selector="subBrandLink">
                    {subBrand} 
                    </Link>                
                    <Typography {...styles.nameText} data-test-selector="subBrandSeparator">
                        {translate(" by ")}
                    </Typography> 
                </>
                }
                <Link {...styles.nameLink} href={brand.detailPagePath} data-test-selector="brandLink">
                    {brandDisplay} 
                </Link> 
            </StyledWrapper>
        </StyledWrapper>
    ) : (
        <>
            {subBrand} 
            {translate(" by ")}            
            {brandDisplay}
        </>
    );
};

export default connect(mapStateToProps,mapDispatchToProps)(ProductBrand);
